// printLineDensity.js
// 印刷時の局所的な線密度をクラスタ単位で計算し、密集箇所のみ線幅を自動で細くする

const DENSITY_LOW      = 0.04;   // クラスタ密度がこれ以下なら調整なし
const DENSITY_HIGH     = 0.20;   // クラスタ密度がこれ以上なら最大に細く
const MIN_WIDTH_FACTOR = 0.20;   // 最小線幅倍率

// --- Union-Find ---
class UnionFind {
	constructor(n) { this.parent = Array.from({ length: n }, (_, i) => i); }
	find(x) { return this.parent[x] === x ? x : (this.parent[x] = this.find(this.parent[x])); }
	union(a, b) { this.parent[this.find(a)] = this.find(b); }
}

// GeoJSONソースを持つ手描きlineレイヤーを列挙（GPSトラックは除外）
function getDrawingLineLayers(map) {
	const style = map.getStyle();
	if (!style || !style.layers || !style.sources) return [];
	return style.layers.filter(layer => {
		if (layer.type !== 'line') return false;
		const src = style.sources[layer.source];
		if (!src || src.type !== 'geojson') return false;
		if (layer.source === 'print-area') return false;
		// GPSトラック（track-*）は登山道など長大な線で、密度計算の対象外
		if (layer.source.startsWith('track-')) return false;
		return true;
	});
}

// 2点間の距離(m)
function segmentLength(c1, c2) {
	const midLat = (c1[1] + c2[1]) / 2;
	const dx = (c2[0] - c1[0]) * 111111 * Math.cos(midLat * Math.PI / 180);
	const dy = (c2[1] - c1[1]) * 111111;
	return Math.sqrt(dx * dx + dy * dy);
}

// 座標配列群からバウンディングボックスを算出
function extractLineCoords(geometry) {
	if (!geometry) return [];
	switch (geometry.type) {
		case 'LineString':   return [geometry.coordinates];
		case 'MultiLineString': return geometry.coordinates;
		case 'Polygon':      return geometry.coordinates;
		case 'MultiPolygon': return geometry.coordinates.flat();
		default:             return [];
	}
}

// 座標配列群 → [minLng, minLat, maxLng, maxLat]
function featureBBox(coordArrays) {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const coords of coordArrays) {
		for (const pt of coords) {
			if (pt[0] < minX) minX = pt[0];
			if (pt[1] < minY) minY = pt[1];
			if (pt[0] > maxX) maxX = pt[0];
			if (pt[1] > maxY) maxY = pt[1];
		}
	}
	return [minX, minY, maxX, maxY];
}

// 座標配列群の重心を算出
function centroid(coordArrays) {
	let sumX = 0, sumY = 0, count = 0;
	for (const coords of coordArrays) {
		for (const pt of coords) {
			sumX += pt[0];
			sumY += pt[1];
			count++;
		}
	}
	return count > 0 ? [sumX / count, sumY / count] : [0, 0];
}

// 2つの重心間の距離(m)
function centroidDistanceM(c1, c2) {
	const midLat = (c1[1] + c2[1]) / 2;
	const dx = (c2[0] - c1[0]) * 111111 * Math.cos(midLat * Math.PI / 180);
	const dy = (c2[1] - c1[1]) * 111111;
	return Math.sqrt(dx * dx + dy * dy);
}

// フィーチャーの線分総長(m)を算出
function featureLineLength(coordArrays) {
	let total = 0;
	for (const coords of coordArrays) {
		for (let i = 0; i < coords.length - 1; i++) {
			total += segmentLength(coords[i], coords[i + 1]);
		}
	}
	return total;
}

// BBox面積(m²)を算出
function bboxAreaM2(bbox) {
	const midLat = (bbox[1] + bbox[3]) / 2;
	const w = (bbox[2] - bbox[0]) * 111111 * Math.cos(midLat * Math.PI / 180);
	const h = (bbox[3] - bbox[1]) * 111111;
	return Math.abs(w * h);
}

// 密度 → スケールファクター
function densityToFactor(density) {
	if (density <= DENSITY_LOW) return 1.0;
	if (density >= DENSITY_HIGH) return MIN_WIDTH_FACTOR;
	const t = (density - DENSITY_LOW) / (DENSITY_HIGH - DENSITY_LOW);
	return 1.0 - t * (1.0 - MIN_WIDTH_FACTOR);
}

/**
 * 印刷用に線幅をクラスタ密度に応じて自動調整する
 * 密集クラスタのフィーチャーのみ細くし、疎なクラスタはそのまま
 * @returns {{ restore: Function }}
 */
export function adjustLineWidthsForPrint(map, bounds, scale, widthMm, heightMm, outWidthPx, outHeightPx) {
	const noop = { restore: () => {} };

	const layers = getDrawingLineLayers(map);
	// console.log(`[printLineDensity] found ${layers.length} line layers:`, layers.map(l => l.id)); // クラスタ化確認用
	if (layers.length === 0) return noop;

	const metersPerPixel = (widthMm / 1000 * scale) / outWidthPx;
	const lngSpan = bounds.east - bounds.west;
	const latSpan = bounds.north - bounds.south;
	if (lngSpan <= 0 || latSpan <= 0) return noop;

	// クラスタリングの距離閾値（印刷範囲幅の1/50相当のメートル距離）
	const midLat = (bounds.north + bounds.south) / 2;
	const clusterGapM = (lngSpan / 50) * 111111 * Math.cos(midLat * Math.PI / 180);

	// 1. 全ソースからフィーチャー情報を収集
	const items = []; // { feature, sourceId, coordArrays, bbox, width, lineLen }
	const visitedSources = new Set();
	const sourceOriginals = new Map(); // sourceId → { source, originalData }

	for (const layer of layers) {
		if (visitedSources.has(layer.source)) continue;
		visitedSources.add(layer.source);

		const source = map.getSource(layer.source);
		if (!source) continue;
		if (!source._data) continue;
		const data = source._data;
		if (typeof data === 'string') continue;

		const features = data.type === 'FeatureCollection' ? data.features
			: data.type === 'Feature' ? [data]
			: [];
		if (features.length === 0) continue;

		sourceOriginals.set(layer.source, { source, originalData: data });
		// console.log(`[printLineDensity] source "${layer.source}": ${features.length} features, data type: ${data.type}`); // クラスタ化確認用

		for (const feature of features) {
			const coordArrays = extractLineCoords(feature.geometry);
			if (coordArrays.length === 0) continue;
			const bbox = featureBBox(coordArrays);
			// 印刷範囲外のフィーチャーはスキップ
			if (bbox[2] < bounds.west || bbox[0] > bounds.east ||
			    bbox[3] < bounds.south || bbox[1] > bounds.north) continue;
			const width = feature.properties?.width
				|| feature.properties?.['stroke-width']
				|| 4;
			const lineLen = featureLineLength(coordArrays);
			const center = centroid(coordArrays);
			items.push({ feature, sourceId: layer.source, coordArrays, bbox, width, lineLen, center });
		}
	}

	// console.log(`[printLineDensity] ${items.length} items in print bounds`); // クラスタ化確認用
	if (items.length === 0) return noop;

	// 2. Union-Findで重心距離ベースの近接クラスタリング
	const uf = new UnionFind(items.length);
	for (let i = 0; i < items.length; i++) {
		for (let j = i + 1; j < items.length; j++) {
			if (centroidDistanceM(items[i].center, items[j].center) <= clusterGapM) {
				uf.union(i, j);
			}
		}
	}

	// クラスタごとにアイテムをグループ化
	const clusters = new Map(); // rootId → [index, ...]
	for (let i = 0; i < items.length; i++) {
		const root = uf.find(i);
		if (!clusters.has(root)) clusters.set(root, []);
		clusters.get(root).push(i);
	}

	// 3. クラスタごとの密度とスケールファクターを計算
	const factorPerItem = new Float64Array(items.length).fill(1.0);
	let hasAdjustment = false;

	for (const [, indices] of clusters) {
		// クラスタの結合BBoxを算出
		let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity;
		let totalInk = 0;
		for (const idx of indices) {
			const it = items[idx];
			if (it.bbox[0] < cMinX) cMinX = it.bbox[0];
			if (it.bbox[1] < cMinY) cMinY = it.bbox[1];
			if (it.bbox[2] > cMaxX) cMaxX = it.bbox[2];
			if (it.bbox[3] > cMaxY) cMaxY = it.bbox[3];
			totalInk += it.lineLen * it.width * metersPerPixel;
		}
		const area = bboxAreaM2([cMinX, cMinY, cMaxX, cMaxY]);
		const density = area > 0 ? totalInk / area : 0;
		const factor = densityToFactor(density);

		if (factor < 1.0) {
			hasAdjustment = true;
			for (const idx of indices) factorPerItem[idx] = factor;
		}

		// console.log(`[printLineDensity] cluster(${indices.length} features): density=${density.toFixed(4)}, factor=${factor.toFixed(2)}`); // クラスタ化確認用
	}

	if (!hasAdjustment) return noop;

	// 4. ソースごとにクローンデータを作成し、width を調整
	const restoreActions = [];
	// sourceId → Map<feature, factor>
	const featureFactors = new Map();
	for (let i = 0; i < items.length; i++) {
		if (factorPerItem[i] >= 1.0) continue;
		const { sourceId, feature } = items[i];
		if (!featureFactors.has(sourceId)) featureFactors.set(sourceId, new Map());
		featureFactors.get(sourceId).set(feature, factorPerItem[i]);
	}

	for (const [sourceId, fMap] of featureFactors) {
		const { source, originalData } = sourceOriginals.get(sourceId);
		const origFeatures = originalData.type === 'FeatureCollection' ? originalData.features
			: [originalData];

		const clonedFeatures = origFeatures.map(f => {
			const factor = fMap.get(f);
			if (factor === undefined) return f; // このフィーチャーは変更不要
			const origWidth = f.properties?.width
				|| f.properties?.['stroke-width']
				|| 4;
			return {
				...f,
				properties: {
					...f.properties,
					width: origWidth * factor,
				},
			};
		});

		const clonedData = { ...originalData, features: clonedFeatures };
		source.setData(clonedData);
		restoreActions.push(() => {
			const s = map.getSource(sourceId);
			if (s) s.setData(originalData);
		});
	}

	console.log(`[printLineDensity] adjusted ${restoreActions.length} source(s), ${featureFactors.size} cluster(s)`);

	// クラスタ情報を返す（デバッグ可視化用）
	const clusterInfos = [];
	for (const [, indices] of clusters) {
		let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity;
		for (const idx of indices) {
			const it = items[idx];
			if (it.bbox[0] < cMinX) cMinX = it.bbox[0];
			if (it.bbox[1] < cMinY) cMinY = it.bbox[1];
			if (it.bbox[2] > cMaxX) cMaxX = it.bbox[2];
			if (it.bbox[3] > cMaxY) cMaxY = it.bbox[3];
		}
		clusterInfos.push({
			count: indices.length,
			centerLng: (cMinX + cMaxX) / 2,
			centerLat: (cMinY + cMaxY) / 2,
			radiusLng: (cMaxX - cMinX) / 2,
			radiusLat: (cMaxY - cMinY) / 2,
			factor: factorPerItem[indices[0]],
		});
	}

	return {
		restore: () => { for (const action of restoreActions) action(); },
		clusters: clusterInfos,
	};
}
